import { Mixpanel } from "mixpanel-browser";
import { useState, useEffect, useContext } from "react";
import { useMixpanel } from "react-mixpanel-browser";
import { UserContext } from "../taskpane/components/Auth";
import { Location, Space } from "@graphql";
// Downloaded from unicode-org:
// https://raw.githubusercontent.com/unicode-org/cldr-json/main/cldr-json/cldr-core/supplemental/windowsZones.json
const zones: WindowsZonesType = require("./windowsZones.json");

type WindowsZonesType = {
  supplemental: {
    version: {
      _unicodeVersion: string;
      _cldrVersion: string;
    };
    windowsZones: {
      mapTimezones: {
        mapZone: {
          _other: string;
          _type: string;
          _territory: string;
        };
      }[];
    };
  };
};
/* global Office */

interface Outlook {
  start: Date;
  end: Date;
  outlookTimezone: string;
  setLocationInEvent: (space: Space, location: Location) => void;
  removeLocationsFromEvent: (
    space: Space,
    location: Location,
    callback: (asyncResult: Office.AsyncResult<void>) => void,
  ) => void;
  updateEventStart: (start: Date) => void;
  updateEventEnd: (end: Date) => void;
  locationChanges: (handler: (result: Office.EnhancedLocationsChangedEventArgs) => void) => void;
  locationChangesUnsubscribe: () => void;
}

export function useOutlook(): Outlook {
  const [start, setStart] = useState<Date>();
  const [end, setEnd] = useState<Date>();
  const [outlookTimezone, setOutlookTimezone] = useState<string>();
  const user = useContext(UserContext);
  const mixpanel: Mixpanel = useMixpanel();

  const track = (type: "add" | "remove", space: Space, location: Location) => {
    const source = type === "add" ? "Outlook Add Room" : "Outlook Remove Room";
    mixpanel.track(source, {
      source: "outlook",
      location: location.name,
      space_id: space._id,
      customer_id: user.customerId,
      user_id: user._id,
    });
  };

  const setLocationInEvent = (space: Space, location: Location) => {
    track("add", space, location);
    const locationIdentifier: Office.LocationIdentifier = {
      id: space.outlookId ? space.outlookId : space.email,
      type: Office.MailboxEnums.LocationType.Room,
    };
    Office.context.mailbox.item.enhancedLocation.addAsync([locationIdentifier]);
  };

  const removeLocationsFromEvent = (
    space: Space,
    location: Location,
    callback?: (asyncResult: Office.AsyncResult<void>) => void,
  ) => {
    track("remove", space, location);
    const locationIdentifier: Office.LocationIdentifier = {
      id: space.outlookId ? space.outlookId : space.email,
      type: Office.MailboxEnums.LocationType.Room,
    };
    Office.context.mailbox.item.enhancedLocation.removeAsync([locationIdentifier], callback);
  };

  const updateEventStart = (start: Date) => {
    Office.context.mailbox.item.start.setAsync(start);
  };

  const updateEventEnd = (end: Date) => {
    Office.context.mailbox.item.end.setAsync(end);
  };

  const locationChanges = (handler: (result: Office.EnhancedLocationsChangedEventArgs) => void) => {
    Office.context.mailbox.item.addHandlerAsync(Office.EventType.EnhancedLocationsChanged, handler);
  };

  const locationChangesUnsubscribe = () => {
    Office.context.mailbox.item.removeHandlerAsync(Office.EventType.EnhancedLocationsChanged);
  };

  useEffect(() => {
    Office.context.mailbox.item.start?.getAsync((result) => {
      setStart(new Date(result.value));
    });
    Office.context.mailbox.item.end?.getAsync((result) => {
      setEnd(new Date(result.value));
    });
    Office.context.mailbox.item.addHandlerAsync(Office.EventType.AppointmentTimeChanged, (change) => {
      setStart(new Date(change.start));
      setEnd(new Date(change.end));
    });

    const windowsZone = Office.context.mailbox.userProfile.timeZone;
    const iataZone = zones.supplemental.windowsZones.mapTimezones
      .find((zone) => zone.mapZone._other === windowsZone)
      ?.mapZone._type?.split(" ")?.[0];
    setOutlookTimezone(iataZone);
  }, []);

  return {
    start,
    end,
    outlookTimezone,
    setLocationInEvent,
    updateEventEnd,
    updateEventStart,
    removeLocationsFromEvent,
    locationChanges,
    locationChangesUnsubscribe,
  };
}
